import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { internalAgentApi, getInternalAgentErrorMessage } from '../lib/internalAgentApi';
import './internalDashboards.css';

const REFRESH_MS = 30000;

function score(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '0.00';
  return n.toFixed(2);
}

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

export default function InternalProducerDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [topics, setTopics] = useState([]);
  const [portfolio, setPortfolio] = useState({});
  const [headlines, setHeadlines] = useState([]);
  const [guests, setGuests] = useState([]);
  const [unresolvedThreads, setUnresolvedThreads] = useState([]);
  const [opsOverview, setOpsOverview] = useState(null);
  const [selectedTopicId, setSelectedTopicId] = useState('');
  const [selectedTopicDetail, setSelectedTopicDetail] = useState(null);
  const [producerRunBusy, setProducerRunBusy] = useState(false);
  const [producerRunError, setProducerRunError] = useState('');
  const [producerRunResult, setProducerRunResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const [
        listRes,
        portfolioRes,
        headlinesRes,
        guestsRes,
        unresolvedRes,
        opsRes,
      ] = await Promise.all([
        internalAgentApi.get('/api/internal/topics/list'),
        internalAgentApi.get('/api/internal/topics/portfolio'),
        internalAgentApi.get('/api/internal/topics/headlines'),
        internalAgentApi.get('/api/internal/topics/guests'),
        internalAgentApi.get('/api/internal/topics/unresolved-threads'),
        internalAgentApi.get('/api/internal/ops/overview'),
      ]);

      const topicRows = listRes.data?.topics || [];
      setTopics(topicRows);
      setPortfolio(portfolioRes.data?.buckets || {});
      setHeadlines(headlinesRes.data?.headlines || []);
      setGuests(guestsRes.data?.guest_candidates || []);
      setUnresolvedThreads(unresolvedRes.data?.unresolved_threads || []);
      setOpsOverview(opsRes.data || null);

      if (!selectedTopicId && topicRows[0]?.topic_id) {
        setSelectedTopicId(topicRows[0].topic_id);
      }
      setError('');
    } catch (err) {
      setError(getInternalAgentErrorMessage(err, 'Failed to load Producer system state.'));
    } finally {
      setLoading(false);
    }
  }, [selectedTopicId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    let active = true;
    if (!selectedTopicId) {
      setSelectedTopicDetail(null);
      return () => {
        active = false;
      };
    }
    internalAgentApi
      .get(`/api/internal/topics/${encodeURIComponent(selectedTopicId)}`)
      .then((res) => {
        if (active) setSelectedTopicDetail(res.data || null);
      })
      .catch(() => {
        if (active) setSelectedTopicDetail(null);
      });
    return () => {
      active = false;
    };
  }, [selectedTopicId]);

  const selectedTopic = useMemo(
    () => topics.find((topic) => topic.topic_id === selectedTopicId) || null,
    [topics, selectedTopicId]
  );

  const runProducerCrew = useCallback(async () => {
    setProducerRunBusy(true);
    setProducerRunError('');
    try {
      const payload = {
        topic: selectedTopic?.title || undefined,
        context: selectedTopicDetail ? { topic_id: selectedTopicDetail.topic_id || selectedTopic?.topic_id } : undefined,
      };
      const res = await internalAgentApi.post('/api/producer/run', payload);
      setProducerRunResult(res.data || null);
      await load();
    } catch (err) {
      setProducerRunError(getInternalAgentErrorMessage(err, 'Producer run failed.'));
    } finally {
      setProducerRunBusy(false);
    }
  }, [load, selectedTopic, selectedTopicDetail]);

  const runtime = opsOverview?.runtime_dashboard || {};
  const audience = opsOverview?.audience_dashboard || {};
  const rpm = opsOverview?.rpm_calibration_view?.current_topic_calibration || {};
  const topEvents = safeList(opsOverview?.semantic_intelligence_panel?.top_active_events);

  const rundownQueue = useMemo(
    () =>
      topics.slice(0, 8).map((topic, idx) => ({
        id: topic.topic_id,
        segment: `SEG-${String(idx + 1).padStart(2, '0')}`,
        title: topic.title,
        priority: Number(topic.producer_priority || 0),
        readiness: Number(topic.monetization_confidence || 0),
      })),
    [topics]
  );

  if (loading) {
    return (
      <div className="nova-loading" data-testid="internal-producer-loading">
        <div className="loading-ring" />
        <div className="loading-text">Producer</div>
        <div className="loading-sub">Loading editorial orchestration and diagnostics...</div>
      </div>
    );
  }

  return (
    <div className="nova-app internal-agent-shell" data-testid="internal-producer-dashboard">
      <div className="topbar internal-agent-topbar">
        <div className="topbar-dot" />
        <span className="topbar-logo">Producer</span>
        <div className="topbar-sep" />
        <span className="topbar-sub">EDITORIAL ORCHESTRATION · SHOW PLANNING</span>
        <div className="topbar-spacer" />
        <span className="topbar-regions">{topics.length} TOPICS</span>
        <span className="topbar-sensors">{rundownQueue.length} SEGMENTS</span>
        <span className="topbar-degraded">{unresolvedThreads.length} UNRESOLVED</span>
        <button className="topbar-refresh" type="button" onClick={runProducerCrew} disabled={producerRunBusy}>
          {producerRunBusy ? 'RUNNING…' : 'RUN PRODUCER'}
        </button>
        <button className="topbar-refresh" type="button" onClick={load}>
          REFRESH
        </button>
      </div>

      <div className="nova-body internal-agent-body">
        <aside className="internal-agent-column">
          <section className="panel">
            <h3>Producer Summary</h3>
            <div className="internal-grid kpi-grid">
              <div className="kpi-card"><span>Topic queue</span><strong>{topics.length}</strong></div>
              <div className="kpi-card"><span>Unresolved threads</span><strong>{unresolvedThreads.length}</strong></div>
              <div className="kpi-card"><span>Runtime state</span><strong>{runtime.runtime_state || 'idle'}</strong></div>
              <div className="kpi-card"><span>Current segment</span><strong>{runtime.current_segment || '—'}</strong></div>
              <div className="kpi-card"><span>Next speaker</span><strong>{runtime.next_queued_speaker || '—'}</strong></div>
              <div className="kpi-card"><span>Last update</span><strong>{fmtDate(runtime.updated_at || opsOverview?.generated_at)}</strong></div>
            </div>
          </section>

          <section className="panel">
            <h3>Air Readiness + Diagnostics</h3>
            <div className="meta-list">
              <div><span>Dead-air alerts</span><strong>{safeList(runtime.dead_air_alerts).length}</strong></div>
              <div><span>Interruptions</span><strong>{safeList(runtime.interruption_events).length}</strong></div>
              <div><span>Audience questions</span><strong>{safeList(audience.top_questions).length}</strong></div>
              <div><span>RPM confidence</span><strong>{score(rpm.confidence)}</strong></div>
              <div><span>Monetization confidence</span><strong>{score(rpm.monetization_confidence)}</strong></div>
            </div>
          </section>
        </aside>

        <main className="internal-agent-column internal-agent-column-wide">
          {error && (
            <div className="producer-offline-banner" data-testid="producer-offline-banner" role="alert">
              <div className="producer-offline-icon" aria-hidden="true">!</div>
              <div className="producer-offline-body">
                <div className="producer-offline-headline">Producer offline — click to retry</div>
                <div className="producer-offline-detail">{error}</div>
              </div>
              <button
                type="button"
                className="producer-offline-retry"
                data-testid="producer-offline-retry"
                onClick={load}
                disabled={loading}
              >
                {loading ? 'RETRYING…' : 'RETRY'}
              </button>
            </div>
          )}

          <section className="panel">
            <h3>Rundown / Segment Queue</h3>
            <div className="delta-feed-table producer-rundown-table">
              <div className="delta-feed-row delta-feed-head producer-rundown-row">
                <span>Segment</span>
                <span>Topic</span>
                <span>Priority</span>
                <span>Readiness</span>
                <span>Status</span>
              </div>
              {rundownQueue.map((row) => {
                const ready = row.readiness >= 0.55;
                return (
                  <div className="delta-feed-row producer-rundown-row" key={row.id}>
                    <span className="producer-rundown-seg">{row.segment}</span>
                    <span className="producer-rundown-topic" title={row.title}>{row.title}</span>
                    <span>{score(row.priority)}</span>
                    <span>{score(row.readiness)}</span>
                    <span className={`producer-rundown-status ${ready ? 'ready' : 'hold'}`}>
                      {ready ? 'air-ready' : 'hold'}
                    </span>
                  </div>
                );
              })}
              {rundownQueue.length === 0 && <div className="empty">No segment queue rows available.</div>}
            </div>
          </section>

          <section className="panel">
            <h3>Topic Board</h3>
            <div className="sd-table-wrap">
              <table className="sd-table">
                <thead>
                  <tr>
                    <th>Topic</th>
                    <th>Type</th>
                    <th>Velocity</th>
                    <th>Novelty</th>
                    <th>Controversy</th>
                    <th>Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {topics.map((topic) => (
                    <tr
                      key={topic.topic_id}
                      onClick={() => setSelectedTopicId(topic.topic_id)}
                      className={selectedTopicId === topic.topic_id ? 'selected' : ''}
                    >
                      <td>{topic.title}</td>
                      <td>{topic.topic_type}</td>
                      <td>{score(topic.velocity)}</td>
                      <td>{score(topic.novelty)}</td>
                      <td>{score(topic.controversy)}</td>
                      <td>{score(topic.producer_priority)}</td>
                    </tr>
                  ))}
                  {topics.length === 0 && (
                    <tr><td colSpan={6} className="empty">No topics on the board.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel">
            <h3>Recap / Digest Support</h3>
            <div className="internal-grid two-col">
              <div>
                <h4>Headline digest</h4>
                <div className="delta-digest-list">
                  {headlines.slice(0, 10).map((item) => (
                    <div key={item.topic_id}>
                      <span>{item.topic || item.topic_id}</span>
                      <strong>{safeList(item.title_candidates).slice(0, 2).join(' | ') || 'No headline candidates'}</strong>
                      <small>{safeList(item.segment_openers).slice(0, 1).join('')}</small>
                    </div>
                  ))}
                  {headlines.length === 0 && <div className="empty">No headline digest yet.</div>}
                </div>
              </div>
              <div>
                <h4>Live event digest</h4>
                <div className="delta-digest-list">
                  {topEvents.slice(0, 10).map((item) => (
                    <div key={item.event_id}>
                      <span>{item.signal_type}</span>
                      <strong>{item.region_location || 'global'} · {item.status}</strong>
                      <small>{fmtDate(item.timestamp)}</small>
                    </div>
                  ))}
                  {topEvents.length === 0 && <div className="empty">No live events.</div>}
                </div>
              </div>
            </div>
          </section>
        </main>

        <aside className="internal-agent-column">
          <section className="panel">
            <h3>Topic Detail</h3>
            {producerRunError && <div className="internal-error">{producerRunError}</div>}
            {producerRunResult && (
              <div className="internal-note" data-testid="producer-run-result">
                Producer run source: {producerRunResult.source || 'unknown'} · status: {producerRunResult.status || 'unknown'} · topics:{' '}
                {safeList(producerRunResult.episode_context?.topics).length} · segments:{' '}
                {safeList(producerRunResult.episode_context?.segments).length} · scripts:{' '}
                {safeList(producerRunResult.episode_context?.scripts).length}
              </div>
            )}
            {!selectedTopic || !selectedTopicDetail ? (
              <div className="empty">Select a topic from the board to inspect its detail.</div>
            ) : (
              <div className="meta-list">
                <div><span>Topic</span><strong>{selectedTopic.title}</strong></div>
                <div><span>Why now</span><strong>{selectedTopicDetail.why_now || '—'}</strong></div>
                <div><span>Suggested agents</span><strong>{safeList(selectedTopicDetail.suggested_agents).join(', ') || '—'}</strong></div>
                <div><span>Caution flags</span><strong>{safeList(selectedTopicDetail.caution_flags).join(', ') || 'none'}</strong></div>
              </div>
            )}
          </section>

          <section className="panel">
            <h3>Unresolved Threads</h3>
            <div className="delta-digest-list">
              {unresolvedThreads.slice(0, 14).map((item) => (
                <div key={item.id}>
                  <span>{item.domain || 'general'}</span>
                  <strong>{item.summary || 'No summary'}</strong>
                  <small>{item.due_at ? fmtDate(item.due_at) : 'No due date'}</small>
                </div>
              ))}
              {unresolvedThreads.length === 0 && <div className="empty">No unresolved threads.</div>}
            </div>
          </section>

          <section className="panel">
            <h3>Portfolio + Guest Board</h3>
            <div className="meta-list">
              {['high_reach', 'authority_building', 'contrarian', 'evergreen', 'breaking', 'deep_dive'].map((bucket) => (
                <div key={bucket}>
                  <span>{bucket.replace('_', ' ')}</span>
                  <strong>{safeList(portfolio[bucket]).length}</strong>
                </div>
              ))}
            </div>
            <div className="delta-digest-list">
              {guests.slice(0, 8).map((guest) => (
                <div key={`${guest.guest_id}-${guest.name}`}>
                  <span>{guest.name}</span>
                  <strong>{safeList(guest.related_topics).join(', ') || 'general topics'}</strong>
                  <small>credibility {score(guest.credibility_score)} · pull {score(guest.audience_pull)}</small>
                </div>
              ))}
              {guests.length === 0 && <div className="empty">No guest candidates.</div>}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
